import React, { useMemo } from 'react';
import { Layers, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '@/routes/paths';
import { formatINR } from '@/shared/utils/currency';

interface RawDeal {
  stage?: string;
  value?: number;
  grandTotal?: number;
}

interface SalesFunnelWidgetProps {
  /** Raw deal rows (as returned by GET /api/deals) — bucketed by real `stage`. */
  deals?: RawDeal[];
}

const STAGE_ORDER: { key: string; label: string; barColor: string }[] = [
  { key: 'new_lead', label: 'New Leads', barColor: 'bg-primary' },
  { key: 'contacted', label: 'Contacted', barColor: 'bg-info' },
  { key: 'sampling', label: 'Sampling', barColor: 'bg-warning' },
  { key: 'quotation_sent', label: 'Quotation Sent', barColor: 'bg-primary' },
  { key: 'negotiation', label: 'Negotiation', barColor: 'bg-primary' },
  { key: 'order_confirmed', label: 'Order Confirmed', barColor: 'bg-success' },
];

export const SalesFunnelWidget: React.FC<SalesFunnelWidgetProps> = ({ deals = [] }) => {
  const navigate = useNavigate();

  const stages = useMemo(() => {
    return STAGE_ORDER.map((s) => {
      const rows = deals.filter((d) => d.stage === s.key);
      return {
        ...s,
        count: rows.length,
        value: rows.reduce((sum, d) => sum + (Number(d.grandTotal ?? d.value) || 0), 0),
      };
    });
  }, [deals]);

  const totalDeals = stages.reduce((acc, s) => acc + s.count, 0);
  const totalValue = stages.reduce((acc, s) => acc + s.value, 0);
  const maxDeals = Math.max(...stages.map((s) => s.count), 1);

  return (
    <div className="flex h-full flex-col justify-between rounded-card border border-border bg-card p-5 shadow-xs">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-outline/80 pb-3">
        <div className="flex items-center gap-2">
          <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary-container text-primary">
            <Layers className="h-4 w-4" />
          </div>
          <div>
            <span className="text-body-sm font-semibold uppercase tracking-wider text-on-surface">
              My Funnel
            </span>
            <div className="text-data-xs text-on-surface-subtle">Deals by active stage</div>
          </div>
        </div>

        <button
          type="button"
          onClick={() => navigate(ROUTES.pipeline)}
          className="inline-flex items-center gap-1 text-data-xs font-semibold text-primary hover:underline"
        >
          <span>Pipeline</span>
          <ChevronRight className="h-3 w-3" />
        </button>
      </div>

      {/* Funnel Progress Bars */}
      {totalDeals === 0 ? (
        <div className="my-6 text-center text-body-sm text-on-surface-subtle">No open deals in the funnel yet.</div>
      ) : (
        <div className="my-3 flex flex-col gap-2.5">
          {stages.map((stage) => {
            const widthPercent = stage.count === 0 ? 0 : Math.max(18, Math.round((stage.count / maxDeals) * 100));

            return (
              <div
                key={stage.key}
                onClick={() => navigate(`${ROUTES.pipeline}?stage=${stage.key}`)}
                className="group cursor-pointer rounded-lg p-1.5 transition hover:bg-surface-variant"
              >
                <div className="mb-1 flex items-center justify-between text-body-sm">
                  <span className="font-semibold text-on-surface group-hover:text-primary transition">
                    {stage.label}
                  </span>
                  <div className="flex items-center gap-2">
                    <span className="rounded-md bg-surface-container px-1.5 py-0.5 text-data-xs font-semibold text-on-surface-variant">
                      {stage.count} deals
                    </span>
                    <span className="font-semibold text-on-surface">{formatINR(stage.value)}</span>
                  </div>
                </div>

                <div className="h-2 w-full overflow-hidden rounded-full bg-surface-container">
                  <div
                    className={`h-full rounded-full transition-all duration-500 ${stage.barColor}`}
                    style={{ width: `${widthPercent}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Footer Total */}
      <div className="rounded-lg border border-border bg-surface-variant/70 p-3 flex items-center justify-between text-body-sm">
        <span className="text-on-surface-subtle">Active Funnel Pipeline:</span>
        <span className="font-semibold text-on-surface">
          {totalDeals} Deals · {formatINR(totalValue)}
        </span>
      </div>
    </div>
  );
};
